import { useEffect, useRef, useState } from 'react';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar, Brand, Card, Chip, Icon, Pane, PaneHead, TabBar } from '@/components/kit';
import { useShellChrome, useNarrowViewport } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tenantBrandingApi } from '@/services/tenantBrandingApi';
import { useAuthStore } from '@/stores/useAuthStore';
import { UserRole } from '@/types/user';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SettingsRail } from './SettingsRail';
import { ColorEditor, TextEditor } from './editors';
import { SettingsUnreadable, UnreadablePane } from './Unreadable';
import { ThemeAiModal, type ThemeSeed } from './ThemeAiModal';
import {
  useBranding, useFacility, useSaveBranding, useSaveFacility, type BrandingPatchInput,
} from './useSettings';
import { DARK_PRESETS, DEFAULT_DARK_ID, LIGHT_PRESETS, presetOf, type Preset } from './presets';
import {
  DOC_APPLIES_TO, DOC_TEMPLATES, LOGIN_CHIPS,
  LOGIN_FALLBACK_ACCENT, LOGIN_FALLBACK_PRIMARY,
} from './documents';
import { loginHeroGradient } from '@/config/loginPresets';

/**
 * Clinic manager · Settings ▸ Branding — `g_set_brand.dc.html` (Identity) and
 * `g_set_theme.dc.html` (Theme).
 *
 * ONE SCREEN, FOUR TABS. The two boards are the same screen with a different
 * tab active, which is why they are one component: the top bar, the sub-rail,
 * the tab strip, the unsaved chip and the Save are anchored across all four
 * (Law 3 — a sibling switcher is present on every sibling state).
 *
 * THE PANE IS THE SELECTION, both times. On Identity it is the fields
 * rendered — the board's own caption says "the preview is the fields rendered
 * — it is not a separate setting", so it reads the DRAFT, not the server, and
 * changes as you type. On Theme it is the preset you last clicked, which is
 * what "pick one, the pane opens it" means.
 *
 * LOGIN AND DOCUMENTS are the other two — `g_set_login.dc.html` and
 * `g_set_docs.dc.html`. They share the one draft and the one Save, because the
 * board shares the one "⚠ unsaved" chip across all four tabs: a manager who
 * renames the clinic and then picks a template presses Save once.
 */

type Tab = 'identity' | 'theme' | 'login' | 'documents';

const TABS = [
  { key: 'identity' as const, label: 'Identity' },
  { key: 'theme' as const, label: 'Theme' },
  { key: 'login' as const, label: 'Login' },
  { key: 'documents' as const, label: 'Documents' },
];

const TITLES: Record<Tab, string> = {
  identity: 'Branding ▸ Identity',
  theme: 'Branding ▸ Theme',
  login: 'Branding ▸ Login',
  documents: 'Branding ▸ Documents',
};

interface Draft {
  displayName: string;
  tagline: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  lightId: string;
  darkId: string;
  /**
   * Whether the DARK half is a choice this clinic made or the pair the product
   * falls back to. `themeIdDark` is null on a tenant that never picked one, and
   * a ✓ on that row would assert a decision nobody took — everywhere else on
   * this screen a ✓ means "saved" (round-2 MINOR). The chip still lights,
   * because that palette IS what the app wears after dark, and the pane's
   * "Pairs with …" line names it; only the tick waits for the choice.
   */
  darkChosen: boolean;
  /** Set by the AI recommender; empty until one of its palettes is picked. */
  primaryColor: string | null;
  accentColor: string | null;
  /** The suggestion's own name, so the pane can say which one is docked. */
  seedName: string | null;
  /**
   * THE SIGN-IN HERO'S OWN PAIR. Seeded through the exact fallback chain
   * `LoginPage` itself uses — `loginPrimaryColor` → `primaryColor` → the MAIE
   * default — so these two cards always print the colour the sign-in page
   * PAINTS today, whether or not this clinic ever set one. Saving makes the
   * effective value the stored one, which is what the manager just decided.
   */
  loginPrimary: string;
  loginAccent: string;
  /** The clinic-wide document template, or null when none is stored. */
  pdfTemplate: string | null;
}

export function ManagerBrandingPage() {
  const go = useNavigate();
  const [params, setParams] = useSearchParams();
  /* An address typed or shared with a stale tab name must not take the page down. */
  const asked = params.get('tab') as Tab | null;
  const tab: Tab = asked && asked in TITLES ? asked : 'identity';

  const { branding, isLoading, error: brandingError, refetch: refetchBranding } = useBranding();
  const {
    facility, isLoading: facilityLoading, error: facilityError, refetch: refetchFacility,
  } = useFacility();
  const save = useSaveBranding();
  const saveFacility = useSaveFacility();
  const tenantId = useAuthStore((s) => s.activeTenant?.id ?? null);
  /* WHO MAY UPLOAD AN IMAGE. `POST /tenants/:id/branding/logo` sits under
     `requireOwner()` (tenants-admin.controller.ts:119), not under
     `hospital_settings:write` — so for an employed Clinic Manager the refusal
     is certain before the click, and a card that opens a file picker and only
     then says no has already wasted the choosing (BRIEF §4.8: the mark goes on
     the control, not in the aftermath). The tenant's Owner, who reaches this
     same screen, keeps the live card. */
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const mayUpload = roleCheck.hasRole(UserRole.OWNER);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [base, setBase] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<'name' | 'tagline' | null>(null);
  const [colouring, setColouring] = useState<'loginPrimary' | 'loginAccent' | null>(null);
  /* THE PANE'S SUBJECT ON THE THEME TAB — which preset is open in it. The
     board opens on the LIGHT one that is set, and clicking any chip re-docks. */
  const [opened, setOpened] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [generating, setGenerating] = useState(false);
  const [upload, setUpload] = useState<{ kind: 'logo' | 'favicon'; error?: string } | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const faviconInput = useRef<HTMLInputElement>(null);
  const uploadKind = useRef<'logo' | 'favicon'>('logo');

  usePageMeta({ title: 'Branding · System Settings' });

  /* THE NAME AND THE TAGLINE LIVE IN TWO DIFFERENT ROWS, and the screen has to
     wait for both before it seeds — a draft seeded off branding alone would
     show an empty tagline, and saving it would then blank a real one.
       • name  → `tenant_brandings.displayName` (the service mirrors it onto
         `tenants.displayName`, which is what the rail and the login page read)
       • tagline → `hospital.config.tagline.en`. `tenant_brandings` has no
         tagline column and `saveHospitalConfig` strips the legacy `branding`
         sub-object, so the config's TOP-LEVEL `tagline` is the one place this
         persists. That is why Save writes twice. */
  useEffect(() => {
    if (!branding || !facility || draft) return;
    const seeded: Draft = {
      displayName: branding.displayName ?? '',
      tagline: facility.tagline?.en ?? '',
      logoUrl: branding.logoUrl,
      faviconUrl: branding.faviconUrl,
      lightId: branding.themeId ?? LIGHT_PRESETS[0]?.id ?? 'periwinkle',
      darkId: branding.themeIdDark ?? DEFAULT_DARK_ID,
      darkChosen: !!branding.themeIdDark,
      primaryColor: branding.primaryColor,
      accentColor: branding.accentColor,
      seedName: null,
      loginPrimary:
        branding.loginPrimaryColor || branding.primaryColor || LOGIN_FALLBACK_PRIMARY,
      loginAccent:
        branding.loginAccentColor || branding.accentColor || LOGIN_FALLBACK_ACCENT,
      pdfTemplate: branding.pdfTemplate,
    };
    setDraft(seeded);
    setBase(seeded);
    setOpened(seeded.lightId);
  }, [branding, facility, draft]);

  const dirty = !!draft && !!base && JSON.stringify(draft) !== JSON.stringify(base);
  const patch = (next: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...next } : d));

  /* THE DRAFT IS THE RECORD. It needs BOTH reads (the name is on the branding
     row, the tagline on the facility config), and both sit behind
     `hospital_settings:read` — which every role but Owner and Clinic Manager is
     refused, including the Doctor surface this role folds under. When the reads
     settle with no draft there is nothing to edit, and §4.10 forbids rendering
     the form anyway: the screen says which instead, on both tabs and in the
     pane, and publishes no Save. */
  const reading = (isLoading || facilityLoading) && !draft;
  const unreadable = !reading && !draft;
  const readError = brandingError ?? facilityError;
  const retryRead = () => { void refetchBranding(); void refetchFacility(); };

  const openedPreset: Preset | null = presetOf(opened ?? draft?.lightId);
  const darkPairLabel = presetOf(draft?.darkId)?.label ?? 'the platform’s dark palette';

  const onSave = () => {
    if (!draft) return;
    const body: BrandingPatchInput = {
      displayName: draft.displayName.trim() || undefined,
      logoUrl: draft.logoUrl,
      faviconUrl: draft.faviconUrl,
      themeId: draft.lightId,
      themeIdDark: draft.darkId,
      primaryColor: draft.primaryColor,
      accentColor: draft.accentColor,
      themeConfig: presetOf(draft.lightId)?.theme as unknown as Record<string, unknown> ?? null,
      themeConfigDark: presetOf(draft.darkId)?.theme as unknown as Record<string, unknown> ?? null,
      loginPrimaryColor: draft.loginPrimary,
      loginAccentColor: draft.loginAccent,
      pdfTemplate: draft.pdfTemplate,
    };
    save.mutate(body, { onSuccess: () => setBase(draft) });
    /* The tagline is the other row. Only written when it actually changed, so
       a theme pick does not rewrite the facility config as a side effect. */
    if (facility && (facility.tagline?.en ?? '') !== draft.tagline) {
      saveFacility.mutate({
        ...facility,
        tagline: { en: draft.tagline, ar: facility.tagline?.ar ?? '' },
      });
    }
  };

  const pickFile = (kind: 'logo' | 'favicon') => {
    uploadKind.current = kind;
    (kind === 'logo' ? logoInput : faviconInput).current?.click();
  };

  const onFile = async (file: File | undefined) => {
    if (!file || !tenantId) return;
    const kind = uploadKind.current;
    setUpload({ kind });
    try {
      const { url } = await tenantBrandingApi.uploadLogo(tenantId, file, kind === 'logo' ? 'logo' : 'favicon');
      patch(kind === 'logo' ? { logoUrl: url } : { faviconUrl: url });
      setUpload(null);
    } catch {
      /* THE UPLOAD ROUTE IS THE OWNER'S. `POST /tenants/:id/branding/logo` is
         gated on tenant ownership, not on `hospital_settings:write`, so an
         employed manager is refused — and the card says which, rather than
         swallowing it (reports/clinic-manager.md §3, BACKEND-GAP). */
      setUpload({ kind, error: 'refused — uploading an image is the tenant owner’s to do' });
    }
  };

  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  const q = query.trim().toLowerCase();
  const matches = (text: string) => !q || text.toLowerCase().includes(q);

  /* ── the pane ──────────────────────────────────────────────────────────── */
  const identityPane = (
    <Pane head={<PaneHead overline="LIVE PREVIEW" title="Identity" sub="what the name, tagline and logo make" />}>
      <>
        <Card>
          <div className="flex" style={{ gap: 12 }}>
            {draft?.logoUrl
              ? <img src={draft.logoUrl} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'contain' }} />
              : <Avatar initials={(draft?.displayName || 'M').trim().charAt(0).toUpperCase()} className="cm-av-square" />}
            <div>
              <p className="t-strong">{draft?.displayName || <span className="mut">No name set</span>}</p>
              <p className="t-cap mut">{draft?.tagline || 'No tagline set'}</p>
            </div>
          </div>
          <p className="t-cap mut mt8">Powered by MAIE AI</p>
        </Card>
        <Card>
          <p className="t-ovl mut mb8">Where this shows</p>
          <p className="t-sec sec">
            the app header · the sign-in page · every printed document · the patient portal · the
            message signature
          </p>
        </Card>
        <p className="t-cap mut" style={{ marginTop: 6 }}>
          the preview is the fields rendered — it is not a separate setting
        </p>
      </>
    </Pane>
  );

  const isDarkOpen = DARK_PRESETS.some((p) => p.id === opened);
  const c = openedPreset?.theme.colors;
  const themePane = (
    <Pane head={(
      <PaneHead
        overline={isDarkOpen ? 'DARK PRESET' : 'LIGHT PRESET'}
        title={`${openedPreset?.label ?? 'No preset'}${
          openedPreset && (isDarkOpen
            ? openedPreset.id === draft?.darkId && !!draft?.darkChosen
            : openedPreset.id === draft?.lightId) ? ' ✓' : ''}`}
        sub={(
          <span className="cm-one-line" style={{ display: 'block' }}>
            {openedPreset?.sub || 'the palette the product ships with'}
          </span>
        )}
      />
    )}>
      <>
        <Card>
          <p className="t-ovl mut">Colour slots</p>
          <p className="t-sec sec mt4">
            primary · secondary · accent as shipped — switch to Custom to edit the slots
          </p>
          <p className="t-cap mut" style={{ marginTop: 6 }}>
            {/* Only what the recommender just proposed is an override. The
                tenant's stored primary/accent are the preset's own colours
                mirrored onto the branding row, and calling those "overridden"
                would say every clinic had hand-edited its palette. */}
            {draft?.seedName
              ? `${draft.seedName} — primary ${draft.primaryColor}, accent ${draft.accentColor ?? 'as shipped'}`
              : 'switch to Custom to type your own hexes'}
          </p>
        </Card>
        <Card>
          <p className="t-ovl mut mb8">Status &amp; action colours</p>
          <div className="cm-fields" style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <p className="t-cap mut">Success</p>
              <p className="t-sec w6 grn">{c?.success ?? '—'}</p>
            </div>
            <div style={{ flex: 1 }}>
              <p className="t-cap mut">Warning</p>
              <p className="t-sec w6 amb">{c?.warning ?? '—'}</p>
            </div>
          </div>
          <div className="cm-fields" style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1 }}>
              <p className="t-cap mut">Error</p>
              <p className="t-sec w6 red">{c?.error ?? '—'}</p>
            </div>
            <div style={{ flex: 1 }}>
              <p className="t-cap mut">Info</p>
              <p className="t-sec w6 brand">{c?.info ?? '—'}</p>
            </div>
          </div>
          <p className="t-cap mut mt8">
            shared by every preset — a warning must look like a warning whichever theme is on
          </p>
        </Card>
        <Card kind="ghost">
          <p className="t-sec mut">
            Pairs with <span className="w6 ink">{darkPairLabel}</span> after dark
          </p>
        </Card>
        <p className="t-cap mut" style={{ marginTop: 6 }}>the AI Assistant button uses the Accent</p>
      </>
    </Pane>
  );

  /* ── the Documents pane ────────────────────────────────────────────────
     Law 4: the pane is the SELECTION. The board docks the template whose row
     is selected, so it reads `docSelected` — which opens on the clinic's own
     stored template and re-docks on every row click. */
  const narrow = useNarrowViewport();
  const docSelected =
    DOC_TEMPLATES.find((t) => t.id && t.id === (draft?.pdfTemplate || DOC_TEMPLATES[0].id))
    ?? DOC_TEMPLATES[0];
  const docAccent = draft?.primaryColor || 'var(--brand-500)';
  const docsPane = (
    <Pane head={<PaneHead overline="TEMPLATE" title={docSelected.title} sub={docSelected.sub} />}>
      <>
        <Card>
          {/* `minimal` is the editorial structure with the colour band
              suppressed — `drawEditorialHeader` passes `false` for it — so the
              preview drops the band rather than drawing one that will not
              print. */}
          {docSelected.id === 'minimal' || docSelected.id === 'preprinted' ? null : (
            <div style={{
              height: 5,
              background: docSelected.id === null ? 'var(--stroke-200)' : docAccent,
              borderRadius: 3,
              marginBottom: 10,
            }} />
          )}
          {docSelected.id === 'preprinted' ? (
            <div style={{
              height: 40, marginBottom: 10, borderRadius: 3,
              border: '1px dashed var(--stroke-200)', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <p className="t-cap mut">top 4 cm left blank — your paper’s own letterhead</p>
            </div>
          ) : null}
          <div className="flex" style={{ justifyContent: 'space-between' }}>
            <p className="t-sec w6">{docSelected.id === 'preprinted' ? '' : draft?.displayName || 'This clinic'}</p>
            <p className="t-cap w6 brand">PRESCRIPTION</p>
          </div>
          {/* THE SIGNER'S LINE IS THE SIGNER'S. Nothing in the product records
              a title, a degree list or a syndicate number, so this says what
              the line IS rather than inventing a doctor to fill it. The card
              below names where it would come from. */}
          <p className="t-cap mut mt8">the signer’s title · degrees · syndicate number</p>
          <p className="t-cap mut">▬▬▬▬▬ ▬▬▬ ▬▬</p>
          <p className="t-cap mut">▬▬▬ ▬▬▬▬▬</p>
          <p className="t-cap mut mt12" style={{ textAlign: 'right' }}>signature · stamp ✓</p>
        </Card>
        <Card>
          <p className="t-ovl mut">The doctor block</p>
          <p className="t-sec sec mt4">
            title, degrees, syndicate number and the signature image are read from that doctor’s
            card in Roles &amp; access — the template is the clinic’s, the identity on it is the
            signer’s.{' '}
            <button type="button" className="w6 brand"
              style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
              onClick={() => go('/clinic-manager/settings/roles')}>
              Open Roles &amp; access ›
            </button>
          </p>
        </Card>
        <Card kind="ghost">
          <p className="t-sec mut">Signed artifacts only — a draft never prints a signature.</p>
        </Card>
      </>
    </Pane>
  );

  const crumb = usePhoneCrumb('Settings', TITLES[tab].replace(' ▸ ', ' · '));
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: this pane is part of the task, so it follows the content. */
      paneInline: true,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: TITLES[tab],
      sub: (
        <>
          <button type="button" className="w6 brand"
            style={{ cursor: 'pointer', background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
            onClick={() => go('/clinic-manager/settings')}>
            ‹ System Settings
          </button>
          {' '}<span className="mut">▸</span> {TITLES[tab]}
        </>
      ),
      /* Nothing to search and nothing to save while there is no record —
         both would be §4.8 dead controls. */
      search: unreadable ? null : 'Search a setting…',
      searchValue: query,
      onSearchChange: setQuery,
      subrail: <SettingsRail active="branding" />,
      extra: dirty ? <Chip kind="warn" small className="w6">⚠ unsaved</Chip> : undefined,
      primary: draft
        ? { label: save.isPending ? 'Saving…' : 'Save', onClick: onSave }
        : undefined,
      /* Login has NO pane: the board gives its preview the right half of the
         content column ("full width, the hero beside the fields"), because a
         preview that changes as you type belongs beside the two cards driving
         it, not behind a divider. */
      pane: unreadable
        ? (tab === 'login' ? undefined : <UnreadablePane what="branding" error={readError} />)
        : tab === 'identity' ? identityPane
          : tab === 'theme' ? themePane
            : tab === 'documents' ? docsPane : undefined,
    },
    [
      tab, query, dirty, save.isPending, unreadable, !!draft,
      draft?.displayName ?? '', draft?.tagline ?? '', draft?.logoUrl ?? '',
      draft?.lightId ?? '', draft?.darkId ?? '', opened ?? '',
      draft?.primaryColor ?? '', draft?.darkChosen ?? false,
      draft?.pdfTemplate ?? '', docSelected.title,
    ],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <TabBar items={TABS} active={tab} onSelect={setTab} className="mt4 cm-branding-tabs" label="Branding sections" />

        {reading ? (
          <p className="t-sec mut" style={{ marginTop: 18 }}>Reading this clinic’s branding…</p>
        ) : null}

        {unreadable && (tab === 'identity' || tab === 'theme') ? (
          <SettingsUnreadable what="branding" error={readError} onRetry={retryRead} />
        ) : null}

        {draft && tab === 'identity' ? (
          <>
            <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {matches('hospital name') ? (
                <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('name')}>
                  <p className="t-ovl mut">Hospital name</p>
                  <p className="t-strong mt4">
                    {draft?.displayName || <span className="mut">Not set</span>} <Icon name="pencil" size="sm" />
                  </p>
                </Card>
              ) : null}
              {matches('tagline') ? (
                <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }} onClick={() => setEditing('tagline')}>
                  <p className="t-ovl mut">Tagline</p>
                  <p className="t-strong mt4">
                    {draft?.tagline || <span className="mut">Not set</span>} <Icon name="pencil" size="sm" />
                  </p>
                </Card>
              ) : null}
            </div>

            <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Logo &amp; favicon</p>
            <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <Card kind="ghost" style={{ flex: 1, margin: 0, textAlign: 'center', padding: '22px 12px', cursor: mayUpload ? 'pointer' : 'default' }}
                onClick={mayUpload ? () => pickFile('logo') : undefined}>
                {/* `ic-lg` — the 20px box the artboard draws both ghost cards
                    with (round-2 MAJOR: the default box is 16px). */}
                <span className="mut"><Icon name="photo" size="lg" /></span>
                <p className="t-sec mut mt4">
                  {draft?.logoUrl ? 'Replace logo' : 'Upload logo'}<br />PNG / JPG / SVG · 2 MB
                  {/* The mark rides the size line rather than taking a line of
                      its own: a third line would grow the ghost card 17px and
                      push the closing line off the board's y. */}
                </p>
                {mayUpload && upload?.kind === 'logo' ? (
                  <p className={upload.error ? 't-cap red' : 't-cap mut'}>{upload.error ?? 'uploading…'}</p>
                ) : null}
              </Card>
              <Card kind="ghost" style={{ flex: 1, margin: 0, textAlign: 'center', padding: '22px 12px', cursor: mayUpload ? 'pointer' : 'default' }}
                onClick={mayUpload ? () => pickFile('favicon') : undefined}>
                <span className="mut"><Icon name="photo" size="lg" /></span>
                <p className="t-sec mut mt4">
                  {draft?.faviconUrl ? 'Replace favicon' : 'Favicon'}<br />16 / 32 px
                </p>
                {mayUpload && upload?.kind === 'favicon' ? (
                  <p className={upload.error ? 't-cap red' : 't-cap mut'}>{upload.error ?? 'uploading…'}</p>
                ) : null}
              </Card>
            </div>

            {narrow ? null : <p className="t-sec mut" style={{ marginTop: 12 }}>
              The logo prints on every document the Documents template lays out, and stands at the
              head of the sign-in page.
            </p>}
          </>
        ) : null}

        {draft && tab === 'theme' ? (
          <>
            <Card kind="accent" style={{ cursor: 'pointer' }} onClick={() => setGenerating(true)}>
              <p className="t-strong">
                <Brand mark="symbol" style={{ height: 14, display: 'inline-block', verticalAlign: '-2px', marginInlineEnd: 6 }} />
                Generate a theme with AI
              </p>
              <p className="t-cap mut mt4">
                a brand colour and a vibe → a matched light and dark pair, previewed before it applies
              </p>
            </Card>

            <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>
              {narrow ? 'Light presets' : 'Light presets — pick one, the pane opens it'}
            </p>
            <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {LIGHT_PRESETS.filter((p) => matches(p.label)).map((p) => (
                <Chip key={p.id}
                  kind={p.id === draft?.lightId ? 'brand' : 'outline'} small
                  className={p.id === draft?.lightId ? 'w6' : undefined}
                  onClick={() => { patch({ lightId: p.id }); setOpened(p.id); }}>
                  {p.label}{p.id === draft?.lightId ? ' ✓' : ''}
                </Chip>
              ))}
            </div>

            <p className="t-ovl mut" style={{ margin: '18px 0 8px' }}>Dark presets</p>
            <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {DARK_PRESETS.filter((p) => matches(p.label)).map((p) => (
                <Chip key={p.id}
                  kind={p.id === draft?.darkId ? 'brand' : 'outline'} small
                  className={p.id === draft?.darkId ? 'w6' : undefined}
                  onClick={() => { patch({ darkId: p.id, darkChosen: true }); setOpened(p.id); }}>
                  {p.label}{p.id === draft?.darkId && draft?.darkChosen ? ' ✓' : ''}
                </Chip>
              ))}
            </div>

            {narrow ? null : <p className="t-sec mut" style={{ marginTop: 12 }}>
              Light and dark are chosen as a pair: the app follows the device, so both halves are
              always set.
            </p>}
          </>
        ) : null}

        {draft && tab === 'login' ? (
          <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {narrow ? <p className="t-sec mut" style={{ margin: '4px 0 10px' }}>The sign-in hero only — the in-app theme is untouched</p> : null}
              <Card style={narrow ? { border: 'none', background: 'transparent', padding: 0 } : undefined}>
                {narrow ? null : <p className="t-ovl mut">
                  Login page — the sign-in hero only, the in-app theme untouched
                </p>}
                <div className="cm-fields" style={{ display: 'flex', gap: 8, marginBottom: 0 }}>
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }}
                    onClick={() => setColouring('loginPrimary')}>
                    <p className="t-ovl mut">Login primary</p>
                    <p className="t-strong mt4">{draft.loginPrimary} <Icon name="pencil" size="sm" /></p>
                  </Card>
                  <Card style={{ flex: 1, margin: 0, cursor: 'pointer' }}
                    onClick={() => setColouring('loginAccent')}>
                    <p className="t-ovl mut">Accent</p>
                    <p className="t-strong mt4">{draft.loginAccent} <Icon name="pencil" size="sm" /></p>
                  </Card>
                </div>
                <p className="t-ovl mut" style={{ margin: '12px 0 6px' }}>Presets</p>
                <div className="cm-chiprow" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {LOGIN_CHIPS.filter((c) => matches(c.label)).map((chip) => {
                    const on = chip.primary.toLowerCase() === draft.loginPrimary.toLowerCase()
                      && chip.accent.toLowerCase() === draft.loginAccent.toLowerCase();
                    if (narrow) {
                      return (
                        <button key={chip.id} type="button" aria-label={chip.label} aria-pressed={on}
                          onClick={() => patch({ loginPrimary: chip.primary, loginAccent: chip.accent })}
                          style={{
                            flex: 1, height: 44, borderRadius: 'var(--r-sm)', cursor: 'pointer',
                            border: on ? '2px solid var(--brand-500)' : '1px solid var(--stroke-200)',
                            background: loginHeroGradient(chip.primary, chip.accent),
                          }} />
                      );
                    }
                    return (
                      <Chip key={chip.id} kind={on ? 'brand' : 'outline'} small
                        className={on ? 'w6' : undefined}
                        onClick={() => patch({ loginPrimary: chip.primary, loginAccent: chip.accent })}>
                        {chip.label}{on ? ' ✓' : ''}
                      </Chip>
                    );
                  })}
                </div>
              </Card>
              {narrow ? null : <Card kind="ghost">
                <p className="t-sec mut">
                  Two colours, and nothing else. The sign-in page is the one surface a patient’s
                  family sees before they are a patient — it does not inherit the working theme,
                  and changing it changes nothing inside the app.
                </p>
              </Card>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p className="t-ovl mut" style={{ margin: '0 0 8px' }}>Live preview</p>
              {/* The hero is the SAME gradient `LoginPage` builds from the two
                  colours (`loginHeroGradient`), so this is the sign-in page's
                  own paint and not a drawing of it. */}
              <Card style={{ border: 'none', padding: 0, overflow: 'hidden' }}>
                <div style={{
                  background: loginHeroGradient(draft.loginPrimary, draft.loginAccent),
                  padding: '22px 18px',
                }}>
                  <p className="t-title" style={{ color: '#fff' }}>
                    {draft.displayName || 'This clinic'}
                  </p>
                  <p className="t-sec" style={{ color: draft.loginAccent }}>
                    {draft.tagline || 'No tagline set'}
                  </p>
                  <div style={{
                    background: 'var(--surface-0)',
                    borderRadius: 'var(--r-sm)',
                    padding: '12px 14px',
                    marginTop: 14,
                  }}>
                    <p className="t-cap mut">email</p>
                    <p className="t-cap mut mt4">password</p>
                    <p className="t-sec w6 brand mt8">Sign in</p>
                  </div>
                </div>
              </Card>
              <p className="t-cap mut" style={{ marginTop: 6 }}>
                the preview updates as the two colours change
              </p>
            </div>
          </div>
        ) : null}

        {draft && tab === 'documents' && narrow ? (
          <>
            <Card style={{ marginTop: 14 }}>
              <label className="t-ovl mut" htmlFor="cm-doc-default">DEFAULT TEMPLATE</label>
              <select id="cm-doc-default" className="cm-inp mt8" value={docSelected.id ?? ''}
                onChange={(e) => patch({ pdfTemplate: e.target.value })}>
                {DOC_TEMPLATES.filter((t) => t.id).map((t) => (
                  <option key={t.id} value={t.id ?? ''}>{t.title} — {t.sub}</option>
                ))}
              </select>
              <p className="t-cap mut mt8">{DOC_TEMPLATES.map((t) => t.title).join(' · ')}</p>
            </Card>
            <p className="t-sec mut" style={{ marginTop: 14 }}>Applies to</p>
            <div className="mt8" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {DOC_APPLIES_TO.split(' · ').map((a) => <Chip key={a} small>{a.charAt(0).toUpperCase() + a.slice(1)}</Chip>)}
            </div>
            <p className="t-cap mut" style={{ marginTop: 10 }}>
              doctors can override under their own Settings ▸ Documents
            </p>
          </>
        ) : null}

        {draft && tab === 'documents' && !narrow ? (
          <>
            <table className="dtable">
              <thead>
                <tr>
                  <th style={{ width: '62%' }}>Template</th>
                  <th style={{ width: '38%' }}>Applies to</th>
                </tr>
              </thead>
              <tbody>
                {DOC_TEMPLATES.filter((t) => matches(`${t.title} ${t.sub}`)).map((t) => {
                  const on = !!t.id && t.id === (draft.pdfTemplate || DOC_TEMPLATES[0].id);
                  const built = t.id !== null;
                  return (
                    <tr key={t.title}
                      className={`rowc${docSelected.title === t.title ? ' sel' : ''}`}
                      onClick={built ? () => patch({ pdfTemplate: t.id }) : undefined}
                      style={built ? undefined : { cursor: 'default' }}>
                      <td>
                        <div>
                          <p className="t-strong">
                            {t.title}{on ? ' ✓' : ''}
                            {/* A row that cannot become the clinic's template
                                says so on itself rather than looking live and
                                answering nothing (§4.8). */}
                            {built ? null : <span className="t-cap mut"> soon</span>}
                          </p>
                          <p className="t-cap mut">{t.sub}</p>
                        </div>
                      </td>
                      <td>
                        <span className="t-sec mut">{on ? DOC_APPLIES_TO : '—'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="t-sec mut" style={{ marginTop: 12 }}>
              Doctors can override the template under their own Settings ▸ Documents; nobody can
              override the clinic’s logo or name.
            </p>
          </>
        ) : null}

        {save.isError ? (
          <p className="t-sec red" style={{ marginTop: 10 }}>
            That did not save. Nothing was changed on the server — try again, or reload to see
            what is stored.
          </p>
        ) : null}
        {save.isSuccess && !dirty ? <p className="t-sec grn" style={{ marginTop: 10 }}>Saved.</p> : null}
      </div>

      {/* The two file pickers the ghost cards open. Hidden, never display:none —
          a display:none input cannot be opened by a click in some engines. */}
      <input ref={logoInput} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="sr-only" onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
      <input ref={faviconInput} type="file" accept="image/png,image/x-icon,image/svg+xml"
        className="sr-only" onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />

      <ThemeAiModal
        open={generating}
        onClose={() => setGenerating(false)}
        onPick={(seed: ThemeSeed) => patch({
          primaryColor: seed.primary, accentColor: seed.accent, seedName: seed.name,
        })}
      />

      <ColorEditor
        open={colouring !== null}
        title={colouring === 'loginAccent' ? 'Accent' : 'Login primary'}
        sub={colouring === 'loginAccent'
          ? 'the mid tone of the sign-in hero, and the tagline on it'
          : 'the bright glow in the sign-in hero'}
        value={(colouring === 'loginAccent' ? draft?.loginAccent : draft?.loginPrimary) ?? ''}
        onClose={() => setColouring(null)}
        onDone={(next) => patch(colouring === 'loginAccent'
          ? { loginAccent: next } : { loginPrimary: next })}
      />

      <TextEditor
        open={editing !== null}
        title={editing === 'tagline' ? 'Tagline' : 'Hospital name'}
        sub={editing === 'tagline'
          ? 'the line under the name, on the sign-in page and every document'
          : 'what this clinic is called everywhere its name appears'}
        value={editing === 'tagline' ? (draft?.tagline ?? '') : (draft?.displayName ?? '')}
        onClose={() => setEditing(null)}
        onDone={(next) => patch(editing === 'tagline' ? { tagline: next } : { displayName: next })}
      />
    </div>
  );
}
